import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import {
  Check,
  ChevronLeft,
  Copy,
  Edit2,
  FolderPlus,
  Globe,
  Image as ImageIcon,
  Lock,
  MoreHorizontal,
  RefreshCw,
  Share,
  Trash2,
  X,
} from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Note } from '../types/notes';
import { parseTags } from '../lib/tags';
import { HIGHLIGHTERS, highlighterFor } from '../lib/paper';
import { useNotes } from '../hooks/useNotes';
import { useCollections } from '../hooks/useCollections';
import { NoteCollectionModal } from '../components/NoteCollectionModal';
import { Lightbox } from '../components/Lightbox';
import { MarginNotes } from '../components/MarginNotes';
import { Doodle, EnterOnce, WritingScribble } from '../components/paper';
import { HeaderChip } from '../components/note/HeaderChip';
import { NoteMenu } from '../components/note/NoteMenu';
import { NoteMasthead } from '../components/note/NoteMasthead';
import { PublicLinkRow } from '../components/note/PublicLinkRow';
import { publicNoteUrl } from '../lib/share';
import { GistSticky } from '../components/note/GistSticky';
import { ScanPrint } from '../components/note/ScanPrint';
import { NoteSheet, PageAction } from '../components/note/NoteSheet';
import { toaster } from '../components/ui';

const COLUMN = 'mx-auto w-full max-w-[46rem] px-5 md:px-8';

/** Transcription prose — mobile's markdown look (markdown-math.tsx) at web size. */
const PROSE = [
  'font-body text-[1rem] leading-[1.65] text-on-surface md:text-[1.0625rem]',
  '[&_h1]:font-headline [&_h1]:italic [&_h1]:font-normal [&_h1]:text-[1.4em] [&_h1]:leading-[1.3] [&_h1]:mb-[0.3em] [&_h1]:mt-[0.9em]',
  '[&_h2]:font-headline [&_h2]:italic [&_h2]:font-normal [&_h2]:text-[1.2em] [&_h2]:leading-[1.3] [&_h2]:mb-[0.3em] [&_h2]:mt-[0.9em]',
  '[&_h3]:font-headline [&_h3]:italic [&_h3]:font-normal [&_h3]:text-[1.05em] [&_h3]:leading-[1.3] [&_h3]:mb-[0.3em] [&_h3]:mt-[0.9em]',
  '[&>*:first-child]:mt-0 [&_p]:my-3',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_a]:text-accent [&_a]:no-underline [&_a:hover]:underline',
  '[&_ul]:my-3 [&_ul]:pl-6 [&_ul]:list-disc [&_ol]:my-3 [&_ol]:pl-6 [&_ol]:list-decimal [&_li]:my-1',
  '[&_blockquote]:my-2 [&_blockquote]:ml-0 [&_blockquote]:border-l-[3px] [&_blockquote]:border-solid [&_blockquote]:border-y-0 [&_blockquote]:border-r-0 [&_blockquote]:border-outline-variant [&_blockquote]:pl-3.5 [&_blockquote]:text-on-surface-variant',
  '[&_code]:font-mono [&_code]:text-[0.88em] [&_code]:bg-surface-container [&_code]:px-[5px] [&_code]:py-[2px] [&_code]:rounded-[5px]',
  '[&_pre]:my-3 [&_pre]:bg-surface-container [&_pre]:p-3 [&_pre]:rounded-[10px] [&_pre]:overflow-x-auto [&_pre]:text-[0.88em]',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_table]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[0.95em]',
  '[&_th]:border [&_th]:border-solid [&_th]:border-outline-variant [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left',
  '[&_td]:border [&_td]:border-solid [&_td]:border-outline-variant [&_td]:px-2.5 [&_td]:py-1.5',
  '[&_hr]:my-4 [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-outline-variant',
  '[&_img]:max-w-full [&_img]:rounded-lg',
].join(' ');

/** Headings get a marker swipe in the note's tone that hugs each wrapped line. */
const HEADINGS: Components = {
  h1: ({ children }) => (
    <h1>
      <span className="pb-hl">{children}</span>
    </h1>
  ),
  h2: ({ children }) => (
    <h2>
      <span className="pb-hl">{children}</span>
    </h2>
  ),
  h3: ({ children }) => (
    <h3>
      <span className="pb-hl">{children}</span>
    </h3>
  ),
};

/**
 * A single note — the web twin of mobile's note screen (mobile/app/(app)/note/[noteId].tsx):
 * round header chips, masthead, the gist sticky with the scan print poking over
 * it, the transcription on a sheet of notebook paper, and margin notes.
 */
export const NotePage: React.FC = () => {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const {
    notes,
    isLoading,
    getNote,
    updateNote,
    getNoteImageUrl,
    deleteNotes,
    deleteAnnotation,
    retryNote,
    isRetrying,
    isUpdating,
  } = useNotes();
  const { collections, getCollectionForNote, setNoteCollection, isSettingNoteCollection } = useCollections();
  const [{ isUserNote, note }, setNote] = useState<{ isUserNote: boolean; note: Note | null }>({
    isUserNote: false,
    note: null,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [collectionModalOpen, setCollectionModalOpen] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ open: boolean; rect: DOMRect | null }>({ open: false, rect: null });

  useEffect(() => {
    if (!noteId || isLoading) return;
    const found = notes.find((n) => n.id.toString() === noteId);
    if (found) {
      setNote({ isUserNote: true, note: found });
      return;
    }
    if (!note) {
      getNote(noteId)
        .then((fetched) => setNote({ isUserNote: false, note: fetched }))
        .catch(() => navigate('/dashboard'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId, notes, isLoading, note]);

  const openScan = useCallback((rect: DOMRect | null = null): void => setLightbox({ open: true, rect }), []);
  const closeScan = useCallback((): void => setLightbox((prev) => ({ ...prev, open: false })), []);

  if (!note) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" color="var(--accent)" />
      </div>
    );
  }

  const tags = parseTags(note.tags);
  // Same tone as the tape on this note's inbox card, so the page feels like the card, opened.
  const tone = tags[0] ? highlighterFor(tags[0]) : HIGHLIGHTERS.yellow;
  const imageUrl = getNoteImageUrl(note);
  const currentCollection = isUserNote ? getCollectionForNote(note.id) : null;

  const copyText = (): void => {
    void navigator.clipboard.writeText(note.content ?? '');
    toaster.success({ title: 'Transcription copied.' });
  };

  const publicUrl = publicNoteUrl(note.id);

  const share = async (): Promise<void> => {
    // A private note's link opens for nobody — say so instead of handing it out.
    if (isUserNote && !note.public) {
      toaster.info({
        title: 'This note is private.',
        description: 'Make it public to share a link anyone can open.',
        action: { label: 'Make public', onClick: () => void togglePublic() },
      });
      return;
    }
    const title = note.title ?? 'A note from PaperBrain';
    if (navigator.share) {
      try {
        await navigator.share({ title, url: publicUrl });
        return;
      } catch {
        /* dismissed — fall through to copying the link */
      }
    }
    await navigator.clipboard.writeText(publicUrl);
    toaster.success({ title: 'Link copied.' });
  };

  const startEdit = (): void => {
    setDraft(note.content ?? '');
    setEditing(true);
  };

  const saveEdit = async (): Promise<void> => {
    await updateNote({ ...note, content: draft });
    setEditing(false);
    toaster.success({ title: 'Saved.' });
  };

  const togglePublic = async (): Promise<void> => {
    await updateNote({ ...note, public: !note.public });
    toaster.info({ title: note.public ? 'Note is now private.' : 'Anyone with the link can view.' });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!window.confirm('Delete note? This permanently removes the note and its scan.')) return;
    await deleteNotes([note.id]);
    toaster.info({ title: 'Note deleted.' });
    navigate('/dashboard');
  };

  const onRetry = async (): Promise<void> => {
    try {
      await retryNote(note.id);
      toaster.info({ title: 'Retrying transcription…' });
    } catch {
      toaster.error({ title: 'Could not retry right now. Try again shortly.' });
    }
  };

  const handleSaveCollection = async (nextCollectionId: number | null): Promise<void> => {
    setCollectionError(null);
    try {
      await setNoteCollection({ noteId: note.id, collectionId: nextCollectionId });
      setCollectionModalOpen(false);
      toaster.success({
        title: 'Collection updated',
        description:
          nextCollectionId === null
            ? 'Removed from its collection.'
            : `Moved to "${collections.find((c) => c.id === nextCollectionId)?.name ?? 'collection'}".`,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setCollectionError(typeof detail === 'string' ? detail : 'Could not update collection. Try again.');
    }
  };

  return (
    <div className="pb-24 pt-[calc(0.75rem+var(--mobile-nav-offset))] md:pt-8">
      <div className={`${COLUMN} relative flex items-center justify-between pb-2`}>
        <HeaderChip icon={ChevronLeft} label="Back" onClick={() => navigate(-1)} />
        <div className="flex items-center gap-2">
          {isUserNote && note.processed && (
            <HeaderChip
              icon={editing ? X : Edit2}
              label={editing ? 'Cancel edit' : 'Edit transcription'}
              active={editing}
              onClick={editing ? () => setEditing(false) : startEdit}
            />
          )}
          <HeaderChip icon={Share} label="Share" onClick={() => void share()} />
          {isUserNote && <HeaderChip icon={MoreHorizontal} label="More options" onClick={() => setMenuOpen(true)} />}
        </div>
        <NoteMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          items={[
            { icon: ImageIcon, label: 'View scan', onClick: () => openScan(null) },
            {
              icon: FolderPlus,
              label: currentCollection ? `Move from “${currentCollection.name}”` : 'Add to collection',
              onClick: () => setCollectionModalOpen(true),
            },
            {
              icon: note.public ? Lock : Globe,
              label: note.public ? 'Make private' : 'Make public',
              onClick: () => void togglePublic(),
            },
            { icon: Trash2, label: 'Delete note', onClick: () => void confirmDelete(), destructive: true },
          ]}
        />
      </div>

      {note.failed ? (
        <div className={`${COLUMN} flex flex-col items-center gap-2 pb-16 pt-16 text-center`}>
          <ScanPrint src={imageUrl} tone={HIGHLIGHTERS.coral} onOpen={openScan} width={120} lifted={lightbox.open} />
          <h2 className="pb-heading-3 m-0 mt-5">This one stumped us</h2>
          <p className="m-0 max-w-sm font-body text-sm text-on-surface-variant">
            We tried a few times but couldn&apos;t read it. A clearer, well-lit photo usually does the trick — or give it
            another go now.
          </p>
          {isUserNote && (
            <button type="button" className="pb-btn pb-btn--primary mt-3" disabled={isRetrying} onClick={() => void onRetry()}>
              <RefreshCw size={16} className={isRetrying ? 'animate-spin' : ''} /> Try again
            </button>
          )}
        </div>
      ) : !note.processed ? (
        <div className={`${COLUMN} flex flex-col items-center gap-2 pb-16 pt-16 text-center`}>
          <ScanPrint src={imageUrl} tone={tone} onOpen={openScan} width={120} lifted={lightbox.open} />
          <WritingScribble width={120} className="mt-6" />
          <p className="m-0 mt-2 font-headline text-[1.3125rem] italic text-on-surface">Reading your handwriting…</p>
          <p className="m-0 font-body text-sm text-on-surface-variant">Usually done in under a minute. Feel free to wander off.</p>
        </div>
      ) : (
        <EnterOnce id={`note-page:${note.id}`} className={`${COLUMN} pt-3`}>
          <NoteMasthead note={note} tags={tags} tone={tone} linkTags={isUserNote} />
          {note.public && <PublicLinkRow url={publicUrl} />}

          <div className="mb-7 mt-12">
            <GistSticky summary={note.summary} scanSrc={imageUrl} tone={tone} lifted={lightbox.open} onOpenScan={openScan} />
          </div>

          <NoteSheet
            label={editing ? 'Editing' : 'Transcription'}
            actions={editing ? null : <PageAction icon={Copy} label="Copy" onClick={copyText} />}
          >
            {editing ? (
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                autoFocus
                placeholder="Write it out…"
                className="block min-h-[420px] w-full resize-y border-none bg-transparent py-2 font-body text-[1rem] leading-[1.65] text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            ) : (
              <div
                className={PROSE}
                style={{ '--tone': tone.fill } as React.CSSProperties}
                onDoubleClick={isUserNote ? startEdit : undefined}
              >
                {note.content ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={HEADINGS}>
                    {note.content}
                  </ReactMarkdown>
                ) : (
                  <p className="italic text-on-surface-variant">No transcription yet.</p>
                )}
              </div>
            )}
          </NoteSheet>

          <MarginNotes
            annotations={note.annotations ?? []}
            onDelete={
              isUserNote
                ? async (a) => {
                    try {
                      await deleteAnnotation({ noteId: note.id, annotationId: a.id });
                    } catch {
                      toaster.error({ title: 'Could not erase that margin note.' });
                    }
                  }
                : undefined
            }
          />

          {!isUserNote && (
            <div className="mt-14 flex flex-col items-center gap-2 text-center">
              <Doodle kind="notebooks" size={0.6} />
              <p className="m-0 font-body text-sm text-on-surface-variant">
                Shared from <span className="font-headline text-base italic text-on-surface">PaperBrain</span> — your
                handwritten notes, searchable.
              </p>
            </div>
          )}
        </EnterOnce>
      )}

      {editing && (
        <div
          className="fixed left-5 right-5 z-40 mx-auto max-w-[42rem] md:left-[calc(15.5rem+2rem)] md:right-8"
          style={{ bottom: 'calc(var(--tab-bar-clearance) + 1.25rem)' }}
        >
          <button
            type="button"
            className="pb-btn pb-btn--primary w-full py-3.5 shadow-[0_4px_12px_rgba(28,27,25,0.2)]"
            onClick={() => void saveEdit()}
            disabled={isUpdating}
          >
            <Check size={18} /> Save changes
          </button>
        </div>
      )}

      {isUserNote && (
        <NoteCollectionModal
          isOpen={collectionModalOpen}
          onClose={() => {
            if (isSettingNoteCollection) return;
            setCollectionModalOpen(false);
            setCollectionError(null);
          }}
          collections={collections}
          currentCollectionId={currentCollection?.id ?? null}
          onSave={handleSaveCollection}
          isSaving={isSettingNoteCollection}
          errorMessage={collectionError}
        />
      )}

      {lightbox.open && (
        <Lightbox src={imageUrl} alt={note.filename} ariaLabel="Original scan" triggerRect={lightbox.rect} onClose={closeScan} />
      )}
    </div>
  );
};
